import React, { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { ScrollPicker } from "./scroll-picker";
import "./date-selector.scss";

const SHORT_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

interface DateSelectorValue {
  month: number;
  day: number;
  year: number;
}

interface DateSelectorChangeData extends DateSelectorValue {
  iso: string;
}

export interface DateSelectorProps {
  label: string;
  labelAddon?: React.ReactNode;
  inputMode?: "manual" | "scroll";
  monthFormat?: "numeric" | "short";
  value?: DateSelectorValue;
  defaultValue?: DateSelectorValue;
  onChange?: (data: DateSelectorChangeData | null) => void;
  minYear?: number;
  maxYear?: number;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  error?: string;
  hasError?: boolean;
  hint?: string;
  className?: string;
  id?: string;
  monthPlaceholder?: string;
  dayPlaceholder?: string;
  yearPlaceholder?: string;
}

function getDaysInMonth(month: number, year: number): number {
  if (month < 1 || month > 12) return 31;
  const y = year > 0 ? year : 2000;
  return new Date(y, month, 0).getDate();
}

function toIso(month: number, day: number, year: number): string {
  const mm = String(month).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  const yyyy = String(year).padStart(4, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function padTwo(n: number): string {
  return String(n).padStart(2, "0");
}

function monthToDisplay(month: number, format: "numeric" | "short"): string {
  if (month < 1 || month > 12) return "";
  if (format === "short") return SHORT_MONTHS[month - 1] ?? "";
  return padTwo(month);
}

function parseMonthInput(raw: string, format: "numeric" | "short"): number | null {
  if (format === "short") {
    const idx = SHORT_MONTHS.findIndex(
      (m) => m.toLowerCase() === raw.trim().toLowerCase()
    );
    return idx >= 0 ? idx + 1 : null;
  }
  const n = parseInt(raw, 10);
  if (isNaN(n)) return null;
  return n >= 1 && n <= 12 ? n : null;
}

export function DateSelector({
  label,
  labelAddon,
  inputMode = "manual",
  monthFormat = "numeric",
  value,
  defaultValue,
  onChange,
  minYear = 1900,
  maxYear,
  required = false,
  disabled = false,
  readOnly = false,
  error,
  hasError = false,
  hint,
  className,
  id,
  monthPlaceholder,
  dayPlaceholder,
  yearPlaceholder,
}: DateSelectorProps) {
  const resolvedMaxYear = maxYear ?? new Date().getFullYear();

  const initial = value ?? defaultValue;
  const [monthRaw, setMonthRaw] = useState(
    initial?.month ? monthToDisplay(initial.month, monthFormat) : ""
  );
  const [dayRaw, setDayRaw] = useState(
    initial?.day ? padTwo(initial.day) : ""
  );
  const [yearRaw, setYearRaw] = useState(
    initial?.year ? String(initial.year) : ""
  );

  const [monthVal, setMonthVal] = useState(initial?.month ?? 0);
  const [dayVal, setDayVal] = useState(initial?.day ?? 0);
  const [yearVal, setYearVal] = useState(initial?.year ?? 0);

  // Refs that always hold the latest raw value, updated synchronously in
  // change handlers so that blur handlers never see stale state.
  const monthRawRef = useRef(monthRaw);
  const dayRawRef = useRef(dayRaw);
  const yearRawRef = useRef(yearRaw);
  const monthValRef = useRef(monthVal);
  const dayValRef = useRef(dayVal);
  const yearValRef = useRef(yearVal);

  const [fieldErrors, setFieldErrors] = useState<{
    month?: string;
    day?: string;
    year?: string;
  }>({});

  const [openPicker, setOpenPicker] = useState<"month" | "day" | "year" | null>(
    null
  );

  const monthRef = useRef<HTMLInputElement>(null);
  const dayRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  // Sync controlled value
  useEffect(() => {
    if (value) {
      setMonthVal(value.month);
      setDayVal(value.day);
      setYearVal(value.year);
      monthValRef.current = value.month;
      dayValRef.current = value.day;
      yearValRef.current = value.year;

      const mRaw = monthToDisplay(value.month, monthFormat);
      const dRaw = value.day ? padTwo(value.day) : "";
      const yRaw = value.year ? String(value.year) : "";
      setMonthRaw(mRaw);
      setDayRaw(dRaw);
      setYearRaw(yRaw);
      monthRawRef.current = mRaw;
      dayRawRef.current = dRaw;
      yearRawRef.current = yRaw;
    }
  }, [value, monthFormat]);

  const emitChange = useCallback(
    (m: number, d: number, y: number) => {
      if (!onChange) return;
      if (m >= 1 && m <= 12 && d >= 1 && y >= minYear && y <= resolvedMaxYear) {
        const maxD = getDaysInMonth(m, y);
        if (d <= maxD) {
          onChange({ month: m, day: d, year: y, iso: toIso(m, d, y) });
          return;
        }
      }
      onChange(null);
    },
    [onChange, minYear, resolvedMaxYear]
  );

  // --- Validation ---
  const validateMonth = useCallback(
    (raw: string): string | undefined => {
      if (!raw) return required ? "Required" : undefined;
      const parsed = parseMonthInput(raw, monthFormat);
      if (parsed === null) return "Invalid month";
      return undefined;
    },
    [monthFormat, required]
  );

  const validateDay = useCallback(
    (raw: string, month: number, year: number): string | undefined => {
      if (!raw) return required ? "Required" : undefined;
      const n = parseInt(raw, 10);
      if (isNaN(n) || n < 1) return "Invalid day";
      const max = getDaysInMonth(month || 1, year || 2000);
      if (n > max) return "Invalid day";
      return undefined;
    },
    [required]
  );

  const validateYear = useCallback(
    (raw: string): string | undefined => {
      if (!raw) return required ? "Required" : undefined;
      const n = parseInt(raw, 10);
      if (isNaN(n)) return "Invalid year";
      if (n < minYear || n > resolvedMaxYear)
        return `Year must be between ${minYear} and ${resolvedMaxYear}`;
      return undefined;
    },
    [minYear, resolvedMaxYear, required]
  );

  // --- Manual handlers ---
  const handleMonthChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      if (monthFormat === "numeric") {
        if (raw && !/^\d{0,2}$/.test(raw)) return;
      } else {
        if (raw && !/^[a-zA-Z]{0,3}$/.test(raw)) return;
      }

      setMonthRaw(raw);
      monthRawRef.current = raw;
      setFieldErrors((prev) => ({ ...prev, month: undefined }));

      const parsed = parseMonthInput(raw, monthFormat);
      if (parsed !== null) {
        setMonthVal(parsed);
        monthValRef.current = parsed;
      }

      const maxLen = monthFormat === "numeric" ? 2 : 3;
      if (raw.length >= maxLen && parsed !== null) {
        setTimeout(() => dayRef.current?.focus(), 0);
      }
    },
    [monthFormat]
  );

  const handleDayChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      if (raw && !/^\d{0,2}$/.test(raw)) return;

      setDayRaw(raw);
      dayRawRef.current = raw;
      setFieldErrors((prev) => ({ ...prev, day: undefined }));

      const n = parseInt(raw, 10);
      if (!isNaN(n) && n >= 0) {
        setDayVal(n);
        dayValRef.current = n;
      }

      if (raw.length >= 2) {
        setTimeout(() => yearRef.current?.focus(), 0);
      }
    },
    []
  );

  const handleYearChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      if (raw && !/^\d{0,4}$/.test(raw)) return;

      setYearRaw(raw);
      yearRawRef.current = raw;
      setFieldErrors((prev) => ({ ...prev, year: undefined }));

      const n = parseInt(raw, 10);
      if (!isNaN(n)) {
        setYearVal(n);
        yearValRef.current = n;
      }
    },
    []
  );

  // Blur handlers read from refs so they always see the latest value,
  // even when blur is triggered by auto-advance focus in the same tick.
  const handleMonthBlur = useCallback(() => {
    const raw = monthRawRef.current;
    const err = validateMonth(raw);
    setFieldErrors((prev) => ({ ...prev, month: err }));
    if (!err && raw) {
      const parsed = parseMonthInput(raw, monthFormat);
      if (parsed !== null) {
        const display = monthToDisplay(parsed, monthFormat);
        setMonthVal(parsed);
        setMonthRaw(display);
        monthValRef.current = parsed;
        monthRawRef.current = display;
        emitChange(parsed, dayValRef.current, yearValRef.current);
      }
    }
  }, [monthFormat, validateMonth, emitChange]);

  const handleDayBlur = useCallback(() => {
    const raw = dayRawRef.current;
    const err = validateDay(raw, monthValRef.current, yearValRef.current);
    setFieldErrors((prev) => ({ ...prev, day: err }));
    if (!err && raw) {
      const n = parseInt(raw, 10);
      const display = padTwo(n);
      setDayVal(n);
      setDayRaw(display);
      dayValRef.current = n;
      dayRawRef.current = display;
      emitChange(monthValRef.current, n, yearValRef.current);
    }
  }, [validateDay, emitChange]);

  const handleYearBlur = useCallback(() => {
    const raw = yearRawRef.current;
    const err = validateYear(raw);
    setFieldErrors((prev) => ({ ...prev, year: err }));
    if (!err && raw) {
      const n = parseInt(raw, 10);
      setYearVal(n);
      setYearRaw(String(n));
      yearValRef.current = n;
      yearRawRef.current = String(n);
      emitChange(monthValRef.current, dayValRef.current, n);
    }
  }, [validateYear, emitChange]);

  // --- Scroll picker data ---
  const monthItems = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => ({
      label: monthFormat === "short" ? (SHORT_MONTHS[i] ?? "") : padTwo(i + 1),
      value: i + 1,
    }));
  }, [monthFormat]);

  const dayItems = useMemo(() => {
    const max = getDaysInMonth(monthVal || 1, yearVal || 2000);
    return Array.from({ length: max }, (_, i) => ({
      label: padTwo(i + 1),
      value: i + 1,
    }));
  }, [monthVal, yearVal]);

  const yearItems = useMemo(() => {
    const items = [];
    for (let y = resolvedMaxYear; y >= minYear; y--) {
      items.push({ label: String(y), value: y });
    }
    return items;
  }, [minYear, resolvedMaxYear]);

  // --- Scroll handlers ---
  // Scroll selection updates value but does NOT close the picker.
  // The picker closes only on explicit item click or outside click.
  const handleScrollMonthSelect = useCallback(
    (val: string | number) => {
      const m = Number(val);
      setMonthVal(m);
      monthValRef.current = m;
      const display = monthToDisplay(m, monthFormat);
      setMonthRaw(display);
      monthRawRef.current = display;
      setFieldErrors((prev) => ({ ...prev, month: undefined }));
      emitChange(m, dayValRef.current, yearValRef.current);
    },
    [monthFormat, emitChange]
  );

  const handleScrollDaySelect = useCallback(
    (val: string | number) => {
      const d = Number(val);
      setDayVal(d);
      dayValRef.current = d;
      const display = padTwo(d);
      setDayRaw(display);
      dayRawRef.current = display;
      setFieldErrors((prev) => ({ ...prev, day: undefined }));
      emitChange(monthValRef.current, d, yearValRef.current);
    },
    [emitChange]
  );

  const handleScrollYearSelect = useCallback(
    (val: string | number) => {
      const y = Number(val);
      setYearVal(y);
      yearValRef.current = y;
      setYearRaw(String(y));
      yearRawRef.current = String(y);
      setFieldErrors((prev) => ({ ...prev, year: undefined }));
      emitChange(monthValRef.current, dayValRef.current, y);
    },
    [emitChange]
  );

  const handleFieldClick = useCallback(
    (field: "month" | "day" | "year") => {
      if (disabled || readOnly) return;
      if (inputMode === "scroll") {
        setOpenPicker((prev) => (prev === field ? null : field));
      }
    },
    [inputMode, disabled, readOnly]
  );

  const handlePickerClose = useCallback(() => {
    setOpenPicker(null);
  }, []);

  // --- Derived state ---
  const externalError = error || (hasError ? " " : "");
  const firstFieldError =
    fieldErrors.month || fieldErrors.day || fieldErrors.year;
  const displayError = externalError || firstFieldError;

  const cls = "date-selector";
  const rootClass = [cls, className].filter(Boolean).join(" ");

  const mPlaceholder =
    monthPlaceholder ?? (monthFormat === "short" ? "Mon" : "MM");
  const dPlaceholder = dayPlaceholder ?? "DD";
  const yPlaceholder = yearPlaceholder ?? "YYYY";

  const fieldBase = `${cls}__field`;
  const buildFieldClass = (
    type: "month" | "day" | "year",
    fieldError?: string
  ) => {
    const classes = [fieldBase];
    if (type === "month") {
      classes.push(
        monthFormat === "short"
          ? `${fieldBase}--month-short`
          : `${fieldBase}--month`
      );
    } else if (type === "day") {
      classes.push(`${fieldBase}--day`);
    } else {
      classes.push(`${fieldBase}--year`);
    }
    if (fieldError || externalError) classes.push(`${fieldBase}--error`);
    if (disabled) classes.push(`${fieldBase}--disabled`);
    if (readOnly) classes.push(`${fieldBase}--readonly`);
    if (inputMode === "scroll") classes.push(`${fieldBase}--scroll`);
    return classes.join(" ");
  };

  return (
    <div className={rootClass}>
      {(label != null || labelAddon != null || required) && (
        <div className={`${cls}__label-row`}>
          {label != null && (
            <label htmlFor={id} className={`${cls}__label`}>
              {label}
              {required && (
                <span className={`${cls}__required`} aria-hidden="true">
                  {" "}
                  *
                </span>
              )}
            </label>
          )}
          {labelAddon != null && (
            <span className={`${cls}__label-addon`}>{labelAddon}</span>
          )}
        </div>
      )}

      <div className={`${cls}__field-group`}>
        {/* Month */}
        <div className={`${cls}__field-wrapper`}>
          <input
            ref={monthRef}
            id={id ? `${id}-month` : undefined}
            className={buildFieldClass("month", fieldErrors.month)}
            type="text"
            value={monthRaw}
            placeholder={mPlaceholder}
            onChange={inputMode === "manual" ? handleMonthChange : undefined}
            onBlur={inputMode === "manual" ? handleMonthBlur : undefined}
            onClick={() => handleFieldClick("month")}
            readOnly={readOnly || inputMode === "scroll"}
            disabled={disabled}
            autoComplete="off"
            maxLength={monthFormat === "short" ? 3 : 2}
          />
          {inputMode === "scroll" && openPicker === "month" && (
            <ScrollPicker
              items={monthItems}
              selectedValue={monthVal || undefined}
              onSelect={handleScrollMonthSelect}
              onClose={handlePickerClose}
            />
          )}
        </div>

        {/* Day */}
        <div className={`${cls}__field-wrapper`}>
          <input
            ref={dayRef}
            id={id ? `${id}-day` : undefined}
            className={buildFieldClass("day", fieldErrors.day)}
            type="text"
            value={dayRaw}
            placeholder={dPlaceholder}
            onChange={inputMode === "manual" ? handleDayChange : undefined}
            onBlur={inputMode === "manual" ? handleDayBlur : undefined}
            onClick={() => handleFieldClick("day")}
            readOnly={readOnly || inputMode === "scroll"}
            disabled={disabled}
            autoComplete="off"
            maxLength={2}
          />
          {inputMode === "scroll" && openPicker === "day" && (
            <ScrollPicker
              items={dayItems}
              selectedValue={dayVal || undefined}
              onSelect={handleScrollDaySelect}
              onClose={handlePickerClose}
            />
          )}
        </div>

        {/* Year */}
        <div className={`${cls}__field-wrapper`}>
          <input
            ref={yearRef}
            id={id ? `${id}-year` : undefined}
            className={buildFieldClass("year", fieldErrors.year)}
            type="text"
            value={yearRaw}
            placeholder={yPlaceholder}
            onChange={inputMode === "manual" ? handleYearChange : undefined}
            onBlur={inputMode === "manual" ? handleYearBlur : undefined}
            onClick={() => handleFieldClick("year")}
            readOnly={readOnly || inputMode === "scroll"}
            disabled={disabled}
            autoComplete="off"
            maxLength={4}
          />
          {inputMode === "scroll" && openPicker === "year" && (
            <ScrollPicker
              items={yearItems}
              selectedValue={yearVal || undefined}
              onSelect={handleScrollYearSelect}
              onClose={handlePickerClose}
            />
          )}
        </div>
      </div>

      {displayError && <span className={`${cls}__error`}>{displayError}</span>}
      {!displayError && hint && (
        <span className={`${cls}__hint`}>{hint}</span>
      )}
    </div>
  );
}
